import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';

// Custom styled switch to match theme
const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#487fff',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
    },
}));

// Lock switch with different color
const LockSwitch = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#f59e0b',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
    },
}));

// General Settings Data
const generalSettingsData = [
    { key: 'registers', label: 'Registers' },
    { key: 'accounts', label: 'Accounts' },
    { key: 'multipleInvoice', label: 'Multiple Invoice Generation' },
    { key: 'paymentLinkSms', label: 'Send Payment Link In Invoice And Receipt SMS' },
    { key: 'newInvoiceFormat', label: 'New Invoice Format' },
    { key: 'proformaInvoice', label: 'Proforma Invoice with New Invoice Format' },
    { key: 'parkingPlus', label: 'Parking Plus feature' },
    { key: 'vmsSprintly', label: 'VMS with Sprintly' },
    { key: 'eInvoice', label: 'E-Invoice Feature' },
];

// Email & SMS Settings Data
const emailSmsSettingsData = [
    { feature: 'Notice & Circulars', email: true, sms: true, jointMemberSms: false, watsbox: true, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Forums & Subgroups', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Polling Booth', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Invite Members', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Notices & Minutes Of Meeting', email: true, sms: true, jointMemberSms: false, watsbox: true, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Events', email: true, sms: true, jointMemberSms: false, watsbox: true, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Email & SMS Notifications', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Complaints', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Instructions', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Invoice & Receipts', email: true, sms: true, jointMemberSms: true, watsbox: true, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Documents', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Debit Note', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Credit Note', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'FD Maturity', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Nomination Reminder', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Managing Committee', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Birthday', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Anniversary', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Visitors', email: false, sms: true, jointMemberSms: false, watsbox: false, notification: true, shortNotif: false, longNotif: false },
    { feature: 'Visitors Notification', email: false, sms: false, jointMemberSms: false, watsbox: false, notification: false, shortNotif: true, longNotif: true },
    { feature: 'Visitor Card', email: false, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Classifieds', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
    { feature: 'Staff', email: true, sms: true, jointMemberSms: false, watsbox: false, notification: false, shortNotif: false, longNotif: false },
];

// Lock Settings Data
const lockSettingsData = [
    { key: 'invoiceDetails', label: 'Invoice Details' },
    { key: 'invoiceSettings', label: 'Invoice Settings (Functional & Display)' },
    { key: 'glSettings', label: 'G/L Settings' },
    { key: 'wingsFlats', label: 'Wings & Flats' },
    { key: 'allUploads', label: 'All Uploads Section' },
];

const SocietySettingsLayer = ({ societyName = 'Santosh Society', societyId = '4288' }) => {
    const navigate = useNavigate();

    // State for General Settings
    const [generalSettings, setGeneralSettings] = useState(
        generalSettingsData.reduce((acc, item) => ({ ...acc, [item.key]: false }), {})
    );

    // State for Email & SMS Settings
    const [emailSmsSettings, setEmailSmsSettings] = useState(emailSmsSettingsData);

    // State for Lock Settings
    const [lockSettings, setLockSettings] = useState(
        lockSettingsData.reduce((acc, item) => ({ ...acc, [item.key]: false }), {})
    );

    // Handlers
    const handleGeneralSettingChange = (key) => {
        setGeneralSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleEmailSmsChange = (index, field) => {
        setEmailSmsSettings((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: !item[field] } : item
            )
        );
    };

    const handleLockSettingChange = (key) => {
        setLockSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSave = () => {
        console.log('General Settings:', generalSettings);
        console.log('Email & SMS Settings:', emailSmsSettings);
        console.log('Lock Settings:', lockSettings);
    };

    return (
        <div className="row gy-4">
            <div className="col-lg-12">
                <div className="card">
                    {/* Card Header */}
                    <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <Link onClick={() => navigate(-1)} className="d-inline-block tw-mt-[-3px]">
                                <Icon icon="solar:alt-arrow-left-outline" className="icon text-xl" />
                            </Link>
                            <div>
                                <h5 className="card-title mb-0" style={{ fontWeight: '500' }}>
                                    Community Settings
                                </h5>
                                <span className="text-sm text-secondary-light">
                                    {societyName} (ID: {societyId})
                                </span>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary radius-8 px-16 py-8 text-sm"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary-600 radius-8 px-16 py-8 text-sm"
                                onClick={handleSave}
                            >
                                <Icon icon="mdi:content-save-outline" className="me-4" />
                                Save Settings
                            </button>
                        </div>
                    </div>

                    {/* Card Body */}
                    <div className="card-body" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>

                        {/* General Settings Section */}
                        <div className="mb-32">
                            <div className="d-flex align-items-center gap-2 mb-16">
                                <Icon icon="solar:settings-bold" className="text-primary-600 text-xl" />
                                <h6 className="text-lg fw-semibold mb-0">General Settings</h6>
                            </div>
                            <div className="row g-3">
                                {generalSettingsData.map((item) => (
                                    <div className="col-md-6 col-lg-4" key={item.key}>
                                        <div className="d-flex align-items-center justify-content-between bg-neutral-50 px-16 py-12 radius-8 border">
                                            <span className="text-sm fw-medium text-secondary-light">
                                                {item.label}
                                            </span>
                                            <CustomSwitch
                                                checked={generalSettings[item.key]}
                                                onChange={() => handleGeneralSettingChange(item.key)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Email & SMS Section */}
                        <div className="mb-32">
                            <div className="d-flex align-items-center gap-2 mb-16">
                                <Icon icon="solar:letter-bold" className="text-primary-600 text-xl" />
                                <h6 className="text-lg fw-semibold mb-0">Email & SMS Notifications</h6>
                            </div>
                            <div className="table-responsive">
                                <table className="table bordered-table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="text-sm fw-semibold">Feature</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">Email</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">SMS</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">Joint Member SMS</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">WatsBOX</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">Notification</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">Short Notif.</th>
                                            <th scope="col" className="text-sm fw-semibold text-center">Long Notif.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {emailSmsSettings.map((row, index) => (
                                            <tr key={row.feature}>
                                                <td className="text-sm fw-medium">{row.feature}</td>
                                                <td className="text-center">
                                                    {row.email !== undefined && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.email}
                                                            onChange={() => handleEmailSmsChange(index, 'email')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.sms !== undefined && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.sms}
                                                            onChange={() => handleEmailSmsChange(index, 'sms')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.jointMemberSms && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.jointMemberSms}
                                                            onChange={() => handleEmailSmsChange(index, 'jointMemberSms')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.watsbox && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.watsbox}
                                                            onChange={() => handleEmailSmsChange(index, 'watsbox')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.notification && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.notification}
                                                            onChange={() => handleEmailSmsChange(index, 'notification')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.shortNotif && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.shortNotif}
                                                            onChange={() => handleEmailSmsChange(index, 'shortNotif')}
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {row.longNotif && (
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={row.longNotif}
                                                            onChange={() => handleEmailSmsChange(index, 'longNotif')}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Lock Settings Section */}
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-16">
                                <Icon icon="solar:lock-bold" className="text-warning-600 text-xl" />
                                <h6 className="text-lg fw-semibold mb-0">Lock Settings</h6>
                            </div>
                            <div className="row g-3">
                                {lockSettingsData.map((item) => (
                                    <div className="col-md-6 col-lg-4" key={item.key}>
                                        <div className="d-flex align-items-center justify-content-between bg-neutral-50 px-16 py-12 radius-8 border">
                                            <span className="text-sm fw-medium text-secondary-light">
                                                {item.label}
                                            </span>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-xs text-secondary-light">
                                                    {lockSettings[item.key] ? (
                                                        <Icon icon="solar:lock-bold" className="text-warning-600" />
                                                    ) : (
                                                        <Icon icon="solar:lock-unlocked-outline" className="text-secondary-light" />
                                                    )}
                                                </span>
                                                <LockSwitch
                                                    checked={lockSettings[item.key]}
                                                    onChange={() => handleLockSettingChange(item.key)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SocietySettingsLayer;
